type Gtag = (...args: unknown[]) => void

/**
 * GA4 の user_id を会員IDに紐付ける。
 * - ログイン時: 内部 user.id（非PII・永続）を文字列で送信
 * - 未ログイン/ログアウト時: null を送信（GA4公式推奨。匿名閲覧を会員へ誤紐付けしない）
 *
 * メール・氏名等の個人情報は GA4 へ送らないこと（GA4規約）。
 */
export const setGaUserId = (
  gtag: Gtag,
  user: { id: number } | null | undefined,
): void => {
  gtag('set', { user_id: user ? String(user.id) : null })
}

/**
 * GA4 の page_view を明示送信する。
 * SPAナビゲーション・初回ロードのいずれからも同じ形（page_path 相対パス）で
 * 送ることで、全 page_view に user_id（事前に setGaUserId 済み）と一貫した
 * page_path を確実に載せるための唯一の送信経路。
 * gtag('config') 側の自動 page_view（send_page_view: false）は使わない。
 */
export const sendGaPageView = (
  gtag: Gtag,
  pagePath: string,
  trackingId: string,
): void => {
  gtag('event', 'page_view', { page_path: pagePath, send_to: trackingId })
}

/**
 * GA4 カスタムイベントを送信する。
 * gtag 未ロード(SSR・スクリプトブロック時)は何もしない。
 */
export const sendGaEvent = (
  name: string,
  params: Record<string, unknown> = {},
): void => {
  if (typeof window === 'undefined') return
  const gtag = (window as any).gtag as Gtag | undefined
  if (!gtag) return
  gtag('event', name, params)
}

export const HEARTBEAT_INTERVAL_MS = 15_000
// 可視状態の累計でこの時間に達したら自動停止（放置タブのイベント増殖防止）
const HEARTBEAT_MAX_DURATION_MS = 30 * 60_000
export const HEARTBEAT_MAX_TICKS =
  HEARTBEAT_MAX_DURATION_MS / HEARTBEAT_INTERVAL_MS

/**
 * 滞在時間の運び役となる heartbeat イベントを定期送信する。
 * gtag はどのイベントにも前回送信以降の可視時間(engagement_time_msec)を
 * 自動添付するため、イベント自体にパラメータは不要。
 * セッション最後のページ(別タブで開く詳細プロフィール等)は user_engagement
 * ビーコン不達で滞在時間が0のまま確定するため、定期フラッシュで補う。
 * - タブ非表示中は送らない(可視時間が積まれず無意味なため)。スキップは
 *   maxTicks に数えない
 * - 戻り値は停止関数。ページ遷移時に呼んで新ページで再スタートする
 */
export const startGaHeartbeat = (
  send: () => void,
  isHidden: () => boolean,
  intervalMs: number = HEARTBEAT_INTERVAL_MS,
  maxTicks: number = HEARTBEAT_MAX_TICKS,
): (() => void) => {
  let ticks = 0
  const id = setInterval(() => {
    if (isHidden()) return
    ticks += 1
    send()
    if (ticks >= maxTicks) clearInterval(id)
  }, intervalMs)
  return () => clearInterval(id)
}
